/** Struktur für Systemnotizen, die als „Label: Wert“-Text gespeichert sind. */

export interface SystemNoteFact {
  kind: "field" | "change" | "bullet" | "prose";
  label: string;
  value: string;
  from?: string;
  to?: string;
  /** Ausschnitt im Originaltext, damit Verweise klickbar bleiben. */
  sourceStart: number;
  sourceEnd: number;
}

export interface ParsedSystemNote {
  title: string;
  facts: SystemNoteFact[];
}

interface TitleMatch {
  title: string;
  lead?: {
    kind: "field" | "change";
    label: string;
    value: string;
    from?: string;
    to?: string;
  };
}

const META_LINE = /^(?:Von|Am):\s+\S/;
const ACTOR_LINE = /^(?:Verschoben|Umnummeriert) von .+ am .+\.$/;
const MIRROR_PREFIX =
  /^Hinweis:\s*Diese Notiz wurde aus Auftrag\s+(#[^\s]+)\s+übernommen\.\s*(?:\r?\n)+/;

function matchTitle(line: string): TitleMatch | null {
  const exact: Record<string, string> = {
    "Projekt beauftragt.": "Projekt beauftragt",
    "Projektinfos geändert:": "Projektinfos geändert",
    "Projekt abgelehnt.": "Projekt abgelehnt",
    "Beauftragung zurückgenommen.": "Beauftragung zurückgenommen",
    "Ordner geändert.": "Ordner geändert",
  };
  if (exact[line]) return { title: exact[line] };

  let match = line.match(/^Nachträgliche Stunden (erlaubt|entzogen) für (.+?)\.?$/);
  const permission = match?.[1];
  const person = match?.[2];
  if (permission && person) {
    return {
      title:
        permission === "erlaubt"
          ? "Nachträgliche Stunden erlaubt"
          : "Nachträgliche Stunden entzogen",
      lead: { kind: "field", label: "Für", value: person },
    };
  }

  match = line.match(/^Projektstatus manuell geändert:\s*(.+?)\s+→\s+(.+?)\.?$/);
  const manualFrom = match?.[1];
  const manualTo = match?.[2];
  if (manualFrom && manualTo) {
    return {
      title: "Projektstatus geändert",
      lead: {
        kind: "change",
        label: "Status",
        value: `${manualFrom} → ${manualTo}`,
        from: manualFrom,
        to: manualTo,
      },
    };
  }

  match = line.match(
    /^Projektstatus auf automatische Ableitung zurückgesetzt \(zuvor:\s*(.+?),\s*jetzt:\s*(.+?)\)\.?$/,
  );
  const autoFrom = match?.[1];
  const autoTo = match?.[2];
  if (autoFrom && autoTo) {
    return {
      title: "Automatische Ableitung",
      lead: {
        kind: "change",
        label: "Status",
        value: `${autoFrom} → ${autoTo}`,
        from: autoFrom,
        to: autoTo,
      },
    };
  }

  match = line.match(/^Stunden umgebucht auf (.+)\.$/);
  const rebookTarget = match?.[1];
  if (rebookTarget) {
    return {
      title: "Stunden umgebucht",
      lead: { kind: "field", label: "Auf", value: rebookTarget },
    };
  }

  match = line.match(/^Stunden von (.+?) übernommen(\s*\([^)]*\))?\.?$/);
  const origin = match?.[1];
  if (origin) {
    const extra = (match?.[2] || "").trim();
    return {
      title: "Stunden übernommen",
      lead: {
        kind: "field",
        label: "Herkunft",
        value: extra ? `${origin} ${extra}` : origin,
      },
    };
  }

  match = line.match(/^Doppelte Projektnummer (.+)\.$/);
  const sharedNumber = match?.[1];
  if (sharedNumber) {
    return {
      title: "Doppelte Projektnummer",
      lead: { kind: "field", label: "Nummer", value: sharedNumber },
    };
  }

  match = line.match(/^Dieses Projekt hatte die doppelte Nummer (.+)\.$/);
  const previousNumber = match?.[1];
  if (previousNumber) {
    return {
      title: "Umnummeriert",
      lead: { kind: "field", label: "Nummer", value: previousNumber },
    };
  }

  if (/Zusammenführung\.$/.test(line)) return { title: "Zusammenführung" };
  return null;
}

interface RawLine {
  raw: string;
  start: number;
  end: number;
}

function splitLines(text: string): RawLine[] {
  const lines: RawLine[] = [];
  const re = /\r?\n/g;
  let start = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text))) {
    lines.push({ raw: text.slice(start, match.index), start, end: match.index });
    start = match.index + match[0].length;
  }
  lines.push({ raw: text.slice(start), start, end: text.length });
  return lines;
}

function leadFact(line: RawLine, lead: NonNullable<TitleMatch["lead"]>): SystemNoteFact {
  const at = line.raw.indexOf(lead.value);
  const sourceStart = at >= 0 ? line.start + at : line.start;
  const sourceEnd = at >= 0 ? sourceStart + lead.value.length : line.end;
  return {
    kind: lead.kind,
    label: lead.label,
    value: lead.value,
    from: lead.from,
    to: lead.to,
    sourceStart,
    sourceEnd,
  };
}

function parseBody(lines: RawLine[], firstIndex: number): ParsedSystemNote | null {
  const first = lines[firstIndex];
  if (!first) return null;
  const titleLine = first.raw.trim();
  const matched = matchTitle(titleLine);
  if (!matched) return null;

  const facts: SystemNoteFact[] = [];
  if (matched.lead) facts.push(leadFact(first, matched.lead));

  for (const line of lines.slice(firstIndex + 1)) {
    const body = line.raw.trim();
    if (!body || META_LINE.test(body) || ACTOR_LINE.test(body)) continue;
    const pad = line.raw.indexOf(body);
    const contentStart = line.start + (pad >= 0 ? pad : 0);
    const contentEnd = contentStart + body.length;

    if (body.startsWith("• ")) {
      const value = body.slice(2).trim();
      const marker = line.raw.indexOf(value);
      facts.push({
        kind: "bullet",
        label: "",
        value,
        sourceStart: marker >= 0 ? line.start + marker : contentStart,
        sourceEnd: marker >= 0 ? line.start + marker + value.length : contentEnd,
      });
      continue;
    }

    const change = body.match(/^([^:]{1,56}):\s+(.+?)\s+→\s+(.+)$/);
    const changeLabel = change?.[1];
    const changeFrom = change?.[2];
    const changeTo = change?.[3];
    if (changeLabel && changeFrom && changeTo) {
      const value = `${changeFrom} → ${changeTo}`;
      const at = line.raw.indexOf(value);
      facts.push({
        kind: "change",
        label: changeLabel,
        value,
        from: changeFrom,
        to: changeTo,
        sourceStart: at >= 0 ? line.start + at : contentStart,
        sourceEnd: at >= 0 ? line.start + at + value.length : contentEnd,
      });
      continue;
    }

    const field = body.match(/^([^:]{1,56}):\s+(\S[\s\S]*)$/);
    const fieldLabel = field?.[1];
    const fieldValue = field?.[2];
    if (fieldLabel && fieldValue) {
      const at = line.raw.indexOf(fieldValue);
      facts.push({
        kind: "field",
        label: fieldLabel,
        value: fieldValue,
        sourceStart: at >= 0 ? line.start + at : contentStart,
        sourceEnd: at >= 0 ? line.start + at + fieldValue.length : contentEnd,
      });
      continue;
    }

    facts.push({
      kind: "prose",
      label: "",
      value: body,
      sourceStart: contentStart,
      sourceEnd: contentEnd,
    });
  }

  return { title: matched.title, facts };
}

/** Erkennt gespeicherte Systemnotizen. Normale Chat-Notizen bleiben null. */
export function parseSystemNote(raw: string | null | undefined): ParsedSystemNote | null {
  const text = String(raw || "");
  if (!text.trim()) return null;

  const lines = splitLines(text);
  const mirror = text.match(MIRROR_PREFIX);
  let firstIndex = lines.findIndex((line) => line.raw.trim());
  let takenFrom: SystemNoteFact | null = null;
  if (mirror) {
    const innerStart = mirror[0].length;
    firstIndex = lines.findIndex((line) => line.start >= innerStart && line.raw.trim());
    const token = mirror[1];
    const tokenAt = token ? text.indexOf(token) : -1;
    if (token && tokenAt >= 0) {
      takenFrom = {
        kind: "field",
        label: "Übernommen aus",
        value: `Auftrag ${token}`,
        sourceStart: tokenAt,
        sourceEnd: tokenAt + token.length,
      };
    }
  }
  if (firstIndex < 0) return null;
  const body = parseBody(lines, firstIndex);
  if (!body) return null;
  if (takenFrom) body.facts.unshift(takenFrom);
  return body;
}
